import React, { useEffect, useRef, useState } from 'react';

/**
 * Animated acquisition chain — the visual centerpiece of the hero.
 * Draws a path connecting nodes MARKET → OFFER → MESSAGE → ADS → FUNNEL → FOLLOW-UP → SALES → CUSTOMER.
 * A gold spark travels along the path forever; each node lights up as it passes.
 */
export const AcquisitionChain = () => {
  const nodes = [
    { id: 'market', label: 'MARKET', x: 90, y: 60 },
    { id: 'offer', label: 'OFFER', x: 220, y: 100 },
    { id: 'message', label: 'MESSAGE', x: 120, y: 180 },
    { id: 'ads', label: 'ADS', x: 260, y: 220 },
    { id: 'funnel', label: 'FUNNEL', x: 130, y: 300 },
    { id: 'follow', label: 'FOLLOW-UP', x: 260, y: 350 },
    { id: 'sales', label: 'SALES', x: 130, y: 430 },
    { id: 'customer', label: 'CUSTOMER', x: 260, y: 490, gold: true },
  ];

  // Build a smooth cubic path connecting all nodes
  const pathD = nodes.reduce((acc, node, i) => {
    if (i === 0) return `M ${node.x} ${node.y}`;
    const prev = nodes[i - 1];
    const midY = (prev.y + node.y) / 2;
    return `${acc} C ${prev.x} ${midY}, ${node.x} ${midY}, ${node.x} ${node.y}`;
  }, '');

  const [litNodes, setLitNodes] = useState(new Set());
  const pathRef = useRef(null);
  const sparkRef = useRef(null);
  const rafRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    const path = pathRef.current;
    if (!path) return;

    // Draw the path on mount
    const length = path.getTotalLength();
    path.style.strokeDasharray = `${length} ${length}`;
    path.style.strokeDashoffset = length;
    path.style.transition = 'stroke-dashoffset 1.6s cubic-bezier(0.22, 1, 0.36, 1)';
    // Trigger draw
    requestAnimationFrame(() => {
      path.style.strokeDashoffset = '0';
    });

    // Start spark loop after path drawn
    const startSpark = () => {
      const duration = 4000;
      const animate = (now) => {
        if (!startTimeRef.current) startTimeRef.current = now;
        const elapsed = (now - startTimeRef.current) % duration;
        const progress = elapsed / duration;

        const point = path.getPointAtLength(length * progress);
        if (sparkRef.current) {
          sparkRef.current.setAttribute('cx', point.x);
          sparkRef.current.setAttribute('cy', point.y);
        }

        // Light up nodes as spark passes each one
        const currentDist = length * progress;
        const newLit = new Set();
        nodes.forEach((node, i) => {
          const nodeDist = length * (i / (nodes.length - 1));
          if (currentDist >= nodeDist - 20) newLit.add(node.id);
        });
        setLitNodes(newLit);

        // Reset when loop completes
        if (progress < 0.02) setLitNodes(new Set());

        rafRef.current = requestAnimationFrame(animate);
      };
      rafRef.current = requestAnimationFrame(animate);
    };

    const timer = setTimeout(startSpark, 1600);
    return () => {
      clearTimeout(timer);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative w-full h-full flex items-center justify-center" data-testid="acquisition-chain">
      <svg
        viewBox="0 0 360 550"
        className="w-full h-full max-w-[420px]"
        style={{ overflow: 'visible' }}
      >
        <defs>
          <radialGradient id="chainSpark">
            <stop offset="0" stopColor="#FFF7E3" />
            <stop offset=".45" stopColor="#FFE3A3" />
            <stop offset="1" stopColor="#FFE3A3" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="chainPath" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#E9B44C" stopOpacity="0.9" />
            <stop offset="1" stopColor="#E9B44C" stopOpacity="0.4" />
          </linearGradient>
          <filter id="chainGlow">
            <feGaussianBlur stdDeviation="3" />
          </filter>
        </defs>

        {/* Path */}
        <path
          ref={pathRef}
          d={pathD}
          fill="none"
          stroke="url(#chainPath)"
          strokeWidth="1.5"
          strokeDasharray="0"
          strokeLinecap="round"
        />

        {/* Spark */}
        <g>
          <circle ref={sparkRef} cx="0" cy="0" r="8" fill="url(#chainSpark)" filter="url(#chainGlow)" />
        </g>

        {/* Nodes */}
        {nodes.map((node, i) => {
          const isLit = litNodes.has(node.id);
          const isCustomer = node.gold;
          return (
            <g key={node.id} transform={`translate(${node.x}, ${node.y})`}>
              {isLit && (
                <circle
                  r={isCustomer ? 24 : 18}
                  fill="none"
                  stroke="#E9B44C"
                  strokeWidth="1"
                  opacity="0.4"
                  style={{
                    transformOrigin: 'center',
                    animation: 'nodepulse 1.2s ease-out',
                  }}
                />
              )}
              <rect
                x={-node.label.length * 4 - 10}
                y="-14"
                width={node.label.length * 8 + 20}
                height="28"
                rx="6"
                fill={isCustomer ? '#E9B44C' : (isLit ? 'rgba(233, 180, 76, 0.15)' : 'rgba(255, 255, 255, 0.03)')}
                stroke={isCustomer ? '#E9B44C' : (isLit ? '#E9B44C' : 'rgba(255, 255, 255, 0.12)')}
                strokeWidth="1"
                style={{ transition: 'all 0.4s ease' }}
              />
              <text
                y="4"
                textAnchor="middle"
                fill={isCustomer ? '#1C1405' : (isLit ? '#F4E2BA' : '#9AA3B8')}
                fontFamily="var(--mono)"
                fontSize="10"
                fontWeight={isCustomer ? 700 : 500}
                letterSpacing="0.1em"
                style={{ transition: 'fill 0.4s ease' }}
              >
                {node.label}
              </text>
            </g>
          );
        })}
      </svg>

      <style>{`
        @keyframes nodepulse {
          0% { opacity: 0.8; r: 14; }
          100% { opacity: 0; r: 30; }
        }
      `}</style>
    </div>
  );
};

export default AcquisitionChain;
